import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('rating');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const category = categories.find((c) => c.slug === slug);

  const isSearchMode = slug === 'all' && searchQuery;
  const isAllMode = slug === 'all' && !searchQuery;

  const filteredComics = useMemo(() => {
    let result = [...comics];

    if (isSearchMode) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q)) ||
          c.description.toLowerCase().includes(q)
      );
    } else if (slug && slug !== 'all' && category) {
      result = result.filter((c) => c.category === slug);
    }

    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }

    return result;
  }, [slug, searchQuery, sortBy, category, isSearchMode]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const paginatedComics = filteredComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageTitle = isSearchMode
    ? `搜索"${searchQuery}"的结果`
    : category
      ? category.name
      : '全部漫画';

  const pageDescription = isSearchMode
    ? `韩漫画网中与"${searchQuery}"相关的韩国漫画搜索结果，共找到${filteredComics.length}部作品。`
    : category
      ? `韩漫画网${category.name}分类，${category.description}。浏览精选韩国${category.name}作品，下拉式免费在线观看。`
      : '韩漫画网全部漫画作品列表，涵盖恋爱、热血、奇幻、悬疑等各类型韩国漫画，下拉式免费在线观看。';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `http://nikson.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'http://nikson.cn/',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filteredComics.length,
      itemListElement: paginatedComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          url: `http://nikson.cn/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={`http://nikson.cn/category/${slug}`}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: pageTitle },
            ]}
          />

          <div className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {isSearchMode ? (
                    <>
                      搜索 &ldquo;<span className="text-primary-500">{searchQuery}</span>&rdquo;
                    </>
                  ) : (
                    pageTitle
                  )}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {category?.description ||
                    `共找到 ${filteredComics.length} 部漫画作品`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { value: 'rating' as const, label: '评分最高' },
                  { value: 'views' as const, label: '人气最旺' },
                  { value: 'updated' as const, label: '最近更新' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setCurrentPage(1); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {slug && slug !== 'all' && !isSearchMode && (
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="text-xs text-foreground-500 whitespace-nowrap">分类：</span>
                <Link
                  to="/category/all"
                  className="px-3 py-1.5 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {paginatedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {paginatedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 transition-colors"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                          currentPage === p
                            ? 'bg-foreground-900 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 transition-colors"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">没有找到相关漫画，试试其他关键词吧</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}